import * as React from 'react';
import { type StageErrorSummary } from '@chess/api-insights';
import { CountBars, SkeletonRows, TextSkeleton, type CardProps, type FilterLink } from '@chess/api-insights/charts';
import { CardAside, CardEmpty, CardNote } from '@chess/api-dashboard/react';
import { LOST_PIECES, type LostPiece } from '@chess/api-model';

// The middlegame blunders by the piece each gave away, read from the
// engine's reply: what the reader keeps overlooking. One bar per piece, and
// "Allowed mate" for a forced mate; each bar opens the games with that kind
// of blunder in the library. Blunders that cost position rather than
// material have no bar. Drawn from props only: the card's body, which the
// section draws in the dashboard's frame (`MIDDLEGAME_PIECES_CARD`,
// `registerCard`).

export type MiddlegamePiecesCardProps = CardProps<{ middlegame: StageErrorSummary }, {
  /** Opens the library filtered to the games with that piece given away. */
  link?: FilterLink;
}>;

const LABEL: Record<LostPiece, string> = { pawn: 'Pawn', knight: 'Knight', bishop: 'Bishop', rook: 'Rook', queen: 'Queen', mate: 'Allowed mate' };
const WHAT: Record<LostPiece, string> = { pawn: 'gave away a pawn', knight: 'gave away a knight', bishop: 'gave away a bishop', rook: 'gave away a rook', queen: 'gave away the queen', mate: 'allowed a forced mate' };

/** The frame's title: the registration's and the stories'. */
export const MIDDLEGAME_PIECES_CARD = { title: 'Blunders by piece lost' };
const ASIDE = 'middlegame';

export const MiddlegamePiecesCard: React.FC<MiddlegamePiecesCardProps> = (props) => {
  if (props.loading)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <SkeletonRows labels={LOST_PIECES.map((p) => LABEL[p])} value="0" />
        <CardNote>
          <TextSkeleton sample="3 of 4 middlegame blunders cost material" />
        </CardNote>
      </>
    );
  const { middlegame, link } = props;
  const current = middlegame;
  const material = LOST_PIECES.reduce((n, p) => n + current.lost[p], 0);
  const rows = LOST_PIECES.map((p) => ({
    key: p,
    label: LABEL[p],
    count: current.lost[p],
    title: `${current.lost[p]} ${current.lost[p] === 1 ? 'blunder' : 'blunders'} ${WHAT[p]}, in ${current.lostGames[p]} ${current.lostGames[p] === 1 ? 'game' : 'games'} · opens those games`,
    filter: { blunder: `middlegame-${p}` as const },
  }));
  const note = current.blunders ? `${material} of ${current.blunders} middlegame ${current.blunders === 1 ? 'blunder' : 'blunders'} cost material` : undefined;
  let body: React.ReactNode;
  if (!current.scanned) body = <CardEmpty>Scan games to see what your blunders give away.</CardEmpty>;
  else if (!material) body = <CardEmpty>{current.blunders ? 'Your middlegame blunders cost position, not material.' : 'No middlegame blunders in the last 30 days.'}</CardEmpty>;
  else body = <CountBars rows={rows} link={link} />;
  return (
    <>
      <CardAside>{ASIDE}</CardAside>
      {body}
      <CardNote>{note}</CardNote>
    </>
  );
};
